import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreditCard, Trash2, Star, Loader2 } from 'lucide-react';
import { paymentsAPI } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';

const BRAND_LABEL = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
};

/**
 * Card in the user's dashboard listing saved payment methods.
 * Lets them delete cards or promote one to default. Empty state is friendly.
 */
export default function SavedPaymentMethodsCard() {
  const { language } = useI18n();
  const t = (es, en) => (language === 'es' ? es : en);

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // card object

  const load = async () => {
    setLoading(true);
    try {
      const res = await paymentsAPI.listSavedCards();
      setCards(res.data?.cards || []);
    } catch {
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await paymentsAPI.deleteSavedCard(confirmDelete.id);
      toast.success(t('Tarjeta eliminada', 'Card removed'));
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || t('No se pudo eliminar', 'Could not remove')
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefault = async (card) => {
    if (card.is_default) return;
    setBusyId(card.id);
    try {
      await paymentsAPI.setDefaultSavedCard(card.id);
      toast.success(t('Tarjeta predeterminada actualizada', 'Default card updated'));
      await load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || t('No se pudo actualizar', 'Could not update')
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Card data-testid="saved-payment-methods-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[#F05D5E]" />
            {t('Mis métodos de pago', 'My payment methods')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                {t(
                  'Aún no tienes tarjetas guardadas. Al pagar tu próxima reserva podrás guardar la tarjeta para pagar con 1 tap.',
                  'No saved cards yet. When you pay your next booking you can save the card for 1-tap payments.'
                )}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {cards.map((card) => (
                <li
                  key={card.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border"
                  data-testid={`saved-card-${card.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-12 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground leading-tight">
                        {BRAND_LABEL[card.brand] || card.brand} •••• {card.last4}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('Vence', 'Exp.')} {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}
                        {card.is_default && (
                          <span className="ml-2 text-[10px] uppercase font-semibold text-[#F05D5E]">
                            {t('Predet.', 'Default')}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!card.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(card)}
                        disabled={busyId === card.id}
                        data-testid={`set-default-card-${card.id}`}
                        aria-label={t('Marcar como predeterminada', 'Make default')}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(card)}
                      disabled={busyId === card.id}
                      data-testid={`delete-saved-card-${card.id}`}
                      aria-label={t('Eliminar tarjeta', 'Remove card')}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('¿Eliminar esta tarjeta?', 'Remove this card?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  {BRAND_LABEL[confirmDelete.brand] || confirmDelete.brand} •••• {confirmDelete.last4}
                  <br />
                  {t(
                    'Tendrás que introducir sus datos otra vez si quieres volver a usarla.',
                    'You will need to enter it again next time you want to use it.'
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-card">
              {t('Cancelar', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-card"
            >
              {t('Eliminar', 'Remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
